import React, { useRef } from "react";
import styled from "@emotion/styled";
import { AmountFilter } from "./AmountFilter";
import { TransactionFilter } from "./TransactionFilter";
import { TokenFilter } from "./TokenFilter";
import { NetworkFilter } from "./NetworkFilter";
import { ClearButton } from "./elements/ClearButton";
import { FilterContainer } from "./elements/FilterContainer";
import { CloseIcon } from "components/Icons";
import { Table } from "@tanstack/react-table";
import { TransactionDetail } from "types/history";
import { Filter, FiltersInterface } from "config/consts";
import { FILTERS_INITIAL_STATE } from "components/TableContainer/ActivityTable";
import "../../styles/transitions.css";
import { AnimatePresence } from "framer-motion";

interface FilterBarProps {
  table: Table<TransactionDetail>;
  confirmedOptions: FiltersInterface;
  setConfirmedOptions: React.Dispatch<React.SetStateAction<FiltersInterface>>;
}

export const FilterBar = ({ table, confirmedOptions, setConfirmedOptions }: FilterBarProps) => {
  const hasFilters = Object.values(confirmedOptions).some(value => value != null);
  const nodeRef = useRef(null);
  const updateOptions =
    (filterType: Filter) => (updatedOptions: FiltersInterface[keyof FiltersInterface]) =>
      setConfirmedOptions((s: FiltersInterface) => {
        return { ...s, [filterType]: updatedOptions };
      });

  return (
    <FilterContainer>
      <AnimatePresence>
        {hasFilters && (
          <ClearButton
            variants={fadeVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{
              appear: { duration: 0.5 },
              enter: { duration: 0.3 },
              exit: { duration: 0.3 },
            }}
            role="progressbar"
            ref={nodeRef}
            onClick={() => {
              table.resetColumnFilters();
              setConfirmedOptions(FILTERS_INITIAL_STATE);
            }}
          >
            Clear All <CloseIconStyled />
          </ClearButton>
        )}
      </AnimatePresence>
      <NetworkFilter
        table={table}
        confirmedOptions={confirmedOptions[Filter.Network]}
        setConfirmedOptions={updateOptions(Filter.Network)}
      />
      <TransactionFilter
        table={table}
        confirmedOptions={confirmedOptions[Filter.Transaction]}
        setConfirmedOptions={updateOptions(Filter.Transaction)}
      />
      <TokenFilter
        table={table}
        confirmedOptions={confirmedOptions[Filter.Token]}
        setConfirmedOptions={updateOptions(Filter.Token)}
      />
      <AmountFilter
        table={table}
        confirmedOptions={confirmedOptions[Filter.Amount]}
        setConfirmedOptions={updateOptions(Filter.Amount)}
      />
      {/* TODO: Add date filter later on */}
      {/* <DateFilter
        filterDropdownShown={filterDropdownShown}
        setFilterDropdownShown={setFilterDropdownShown}
      /> */}
    </FilterContainer>
  );
};

// Sub-Components

const CloseIconStyled = styled(CloseIcon)`
  margin-bottom: 2px;
`;

export const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};
